/**
 * I18n Context - Internationalization context for timezone and language settings
 * 
 * Provides:
 * - User's timezone preference
 * - User's language preference
 * - Helper functions for formatting dates in user's timezone
 * - Actions to update preferences
 */

import { createContext, useContext } from "react";
import type { SupportedLocale } from "@nordex/i18n";

/**
 * Clock format type (12-hour or 24-hour)
 */
export type ClockFormat = '12h' | '24h';

/**
 * I18n context value interface
 */
export interface I18nContextValue {
  // State
  /** IANA timezone identifier (e.g., "America/New_York", "UTC") */
  timezone: string;
  /** Locale code (e.g., "en-US", "ar-AE") */
  language: SupportedLocale;
  /** Clock format (12-hour or 24-hour) */
  clockFormat: ClockFormat;
  /** Whether preferences are being loaded from backend */
  isLoading: boolean;

  // Actions
  /** Update user's timezone preference */
  updateTimezone: (timezone: string) => Promise<void>;
  /** Update user's language preference */
  updateLanguage: (language: SupportedLocale) => Promise<void>;
  /** Update user's clock format preference */
  updateClockFormat: (format: ClockFormat) => Promise<void>;

  // Helpers (automatically use user's timezone and clock format)
  /** Format a date in the user's timezone with custom format */
  formatDate: (date: Date | string | number, format?: string) => string;
  /** Format a time for trading display (HH:mm:ss TZ or h:mm:ss a TZ) */
  formatTradingTime: (date: Date | string | number) => string;
  /** Format a date for trade history (MMM d, yyyy HH:mm TZ) */
  formatTradeDate: (date: Date | string | number) => string;
}

/**
 * Default values for the context (used before provider is mounted)
 */
export const DEFAULT_TIMEZONE = "UTC";
export const DEFAULT_LANGUAGE: SupportedLocale = "en-US";
export const DEFAULT_CLOCK_FORMAT: ClockFormat = "24h";

/**
 * I18n context - null by default, set by I18nProvider
 */
export const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Hook to access the I18n context
 * 
 * @throws Error if used outside of I18nProvider
 * @returns I18nContextValue with timezone, language, and helper functions
 * 
 * @example
 * ```tsx
 * const { timezone, formatTradingTime, updateTimezone } = useI18n();
 * 
 * // Format current time in user's timezone
 * const time = formatTradingTime(new Date());
 * 
 * // Update user's timezone
 * await updateTimezone("America/New_York");
 * ```
 */
export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  
  if (context === null) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  
  return context;
};

/**
 * Optional hook that returns null if outside provider (for conditional usage)
 */
export const useI18nOptional = (): I18nContextValue | null => {
  return useContext(I18nContext);
};
